import { type BlockConfig, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `pricingTable` block.
 *
 * Decomposed from the former public Protect pricing section (2-tier monthly/annual) AND
 * `b2b/B2BPricing.tsx` (3-tier Standard/Premium/Enterprise). Optional
 * `commonFeatures` covers Protect's "All plans include" footer and
 * optional `enterpriseCta` covers B2B's "Need a custom solution?" footer.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `pricing-table.tsx` / `pricing-table.payload.ts`
 *   React component export:  `PricingTable`
 *   Payload `slug`:          `pricingTable`
 *   Payload `interfaceName`: `PricingTableBlock`
 */

export const PricingTableBlock: BlockConfig = {
  slug: 'pricingTable',
  interfaceName: 'PricingTableBlock',
  labels: {
    singular: 'Pricing Table',
    plural: 'Pricing Table Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'logoImage',
      type: 'text',
      label: 'Logo image URL',
      admin: {
        description:
          'Optional product logo (e.g. Protect logo) rendered above the headline.',
      },
    },
    {
      name: 'logoAlt',
      type: 'text',
      label: 'Logo alt text',
    },
    {
      name: 'tiers',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 4,
      label: 'Tiers',
      labels: { singular: 'Tier', plural: 'Tiers' },
      fields: [
        { name: 'name', type: 'text', required: true, label: 'Tier name' },
        { name: 'price', type: 'text', required: true, label: 'Price (display)' },
        {
          name: 'priceUnit',
          type: 'text',
          label: 'Price unit',
          admin: {
            description: 'e.g. "/month", "/year". Rendered after the price.',
          },
        },
        {
          name: 'description',
          type: 'text',
          label: 'Tier description',
        },
        {
          name: 'badge',
          type: 'text',
          label: 'Badge label',
          admin: {
            description: 'e.g. "Save 45%", "Most Popular". Rendered above the card.',
          },
        },
        {
          name: 'highlighted',
          type: 'checkbox',
          defaultValue: false,
          label: 'Highlighted tier',
        },
        {
          name: 'ctaText',
          type: 'text',
          required: true,
          label: 'CTA button text',
        },
        linkField({
          name: 'ctaLink',
          label: 'CTA button link',
          required: true,
        }),
        {
          name: 'ctaExternal',
          type: 'checkbox',
          defaultValue: false,
          label: 'CTA external link?',
        },
        {
          name: 'ctaVariant',
          type: 'select',
          label: 'CTA button variant',
          defaultValue: 'default',
          admin: {
            description:
              'Button styling for this tier\u2019s CTA. Defaults to "Default (dark)" to match the original two-tier Protect pricing look. Set to "Primary (yellow)" to highlight a tier (e.g. B2B Premium pricing).',
          },
          options: [
            { label: 'Default (dark)', value: 'default' },
            { label: 'Primary (yellow)', value: 'primary' },
            { label: 'Outline', value: 'outline' },
            { label: 'Ghost', value: 'ghost' },
            { label: 'Inverted (for dark surfaces)', value: 'inverted' },
          ],
        },
        {
          name: 'features',
          type: 'array',
          label: 'Features (per tier)',
          labels: { singular: 'Feature', plural: 'Features' },
          defaultValue: [],
          admin: {
            description:
              'Optional list of feature bullets rendered below the CTA. Leave empty to hide the divider + bullet list.',
          },
          fields: [
            { name: 'text', type: 'text', required: true, label: 'Feature text' },
          ],
        },
      ],
    },
    {
      name: 'commonFeatures',
      type: 'group',
      label: 'Common features (cross-tier footer)',
      admin: {
        description:
          'Optional. Renders only when the items array has at least one entry. Use for "All plans include..." style cross-tier feature lists (e.g. Protect pricing).',
      },
      fields: [
        {
          name: 'heading',
          type: 'text',
          label: 'Heading',
          defaultValue: 'All plans include:',
        },
        {
          name: 'items',
          type: 'array',
          label: 'Items',
          labels: { singular: 'Feature', plural: 'Features' },
          defaultValue: [],
          admin: {
            description:
              'Leave empty to hide the cross-tier footer entirely (heading + grid).',
          },
          fields: [
            { name: 'text', type: 'text', required: true, label: 'Feature text' },
          ],
        },
        {
          name: 'columns',
          type: 'select',
          defaultValue: '2',
          label: 'Columns',
          options: [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
          ],
        },
      ],
    },
    {
      name: 'enterpriseCta',
      type: 'group',
      label: 'Enterprise CTA (footer)',
      admin: {
        description:
          'Optional bottom CTA used by B2B pricing for "Need a custom solution?" upsell. Leave both Button text + Button link blank to render the pricing table without a footer CTA. The block treats this CTA as "present" only when both fields are non-empty.',
      },
      fields: [
        {
          name: 'text',
          type: 'text',
          label: 'Lead-in copy',
        },
        // Button text + link are intentionally NOT marked required so a
        // CMS author can leave the entire enterpriseCta block off without
        // a per-row boolean toggle. The React block guards on both being
        // present before rendering.
        {
          name: 'ctaText',
          type: 'text',
          label: 'Button text',
        },
        linkField({
          name: 'ctaLink',
          label: 'Button link',
        }),
        {
          name: 'ctaExternal',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
      ],
    },
  ],
};

export default PricingTableBlock;
